import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { LayoutDashboard, Briefcase, Users, Settings, LogOut, HelpCircle } from 'lucide-react';
import JobVitalLogo from '../../images/JobVitalLogo.png';

interface CompanySidebarProps {
    isOpen?: boolean;
    onClose?: () => void;
}

export const CompanySidebar: React.FC<CompanySidebarProps> = ({ isOpen: externalIsOpen, onClose }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Use external control if provided, otherwise use internal state
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : isMobileMenuOpen;

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        } else {
            setIsMobileMenuOpen(false);
        }
    }, [onClose]);

    const isActive = (path: string) => location.pathname === path;

    // Close mobile menu on route change
    useEffect(() => {
        handleClose();
    }, [location.pathname, handleClose]);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const handleSignOut = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    const menuItems = [
        {
            path: '/dashboard',
            label: 'Dashboard',
            icon: LayoutDashboard
        },
        {
            path: '/job-postings',
            label: 'Job Postings',
            icon: Briefcase
        },
        {
            path: '/candidates',
            label: 'Candidate Matches',
            icon: Users
        },
        /*
        {
            path: '/candidate-details',
            label: 'Candidate Details',
            icon: UserCheck
        },
        */
        {
            path: '/how-matching-works',
            label: 'How Ranking Works',
            icon: HelpCircle
        },
        {
            path: '/settings',
            label: 'Settings',
            icon: Settings
        },
    ];

    const SidebarContent = () => (
        <>
            {/* Logo Section */}
            <div className="p-6 lg:p-8 flex items-center gap-3">
                <img src={JobVitalLogo} alt="JobVital Logo" className="h-8 w-auto" />
                <span className="text-xl font-bold text-slate-900 tracking-tight">JobVital</span>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-2">
                <div className="px-4 mb-2 text-xs font-semibold text-slate-900 uppercase tracking-wider">
                    Hiring Portal
                </div>
                {menuItems.map((item) => {
                    const active = isActive(item.path);
                    const Icon = item.icon;
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={handleClose}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${active
                                ? 'bg-slate-100 text-slate-900 font-medium shadow-sm'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            <Icon className={`w-5 h-5 ${active ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-600'}`} />
                            <span>{item.label}</span>
                            {active && (
                                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-slate-900"></div>
                            )}
                        </Link>
                    );
                })}
            </nav>

            {/* User Profile Section */}
            <div className="p-4 border-t border-slate-100">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-900 font-bold shadow-sm relative">
                            {user?.email?.charAt(0).toUpperCase()}
                            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">
                                {user?.user_metadata?.full_name || 'Hirer'}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleSignOut}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                    </button>
                </div>
            </div>
        </>
    );

    return (
        <>
            {/* Desktop Sidebar - Hidden on mobile */}
            <div className="hidden lg:flex w-72 bg-white border-r border-slate-100 flex-col h-screen fixed left-0 top-0 z-50 shadow-sm">
                <SidebarContent />
            </div>

            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-2">
                    <img src={JobVitalLogo} alt="JobVital Logo" className="h-7 w-auto" />
                    <span className="text-lg font-bold text-slate-900 tracking-tight">JobVital</span>
                </div>
                <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                    aria-label="Open menu"
                >
                    <svg className="w-6 h-6 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            </div>

            {/* Mobile Sidebar Overlay */}
            {isOpen && (
                <div className="lg:hidden fixed inset-0 z-50">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={handleClose}
                    />

                    {/* Sidebar Panel */}
                    <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl flex flex-col animate-slide-in-left">
                        {/* Close button */}
                        <button
                            onClick={handleClose}
                            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-100 transition-colors z-10"
                            aria-label="Close menu"
                        >
                            <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <SidebarContent />
                    </div>
                </div>
            )}
        </>
    );
};
